import { Link } from "@mui/material";

import { routes } from "@/global/routes";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";
import { faqs, howItWorks } from "./helpers";
import { vendorUUIDs } from "../../constants";

import FindGamesBanner from "public/images/portlandseadogs/Banner.png";
import StaticSeatMap from "public/images/portlandseadogs/SeaDogsMap.png";

import type { TeamConfig } from "../types";

const LogoImage =
  "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/portland-sea-dogs.png";

const config: TeamConfig = {
  teamSchema: "portlandseadogs_flex",
  browserTitle: "Sea Dogs Buy More Earn More Plan",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Portland Sea Dogs",

  dynamicPricing: {
    enabled: true,
    showAllDiscounts: true,
    list: [
      {
        id: 1,
        discount: 0,
        minSelectedAmount: 3,
        updatedAccountGroup: 0,
        customProgressBarMessage:
          "Congrats! You've unlocked your official League x Sea Dogs Collab scarf! Add 2 more games to receive 4 complimentary Opening Day tickets, 7+ games for an exclusive batting practice experience, or checkout now.",
      },
      {
        id: 2,
        discount: 0,
        minSelectedAmount: 4,
        updatedAccountGroup: 0,
        customProgressBarMessage:
          "Add 1 more game for 4 complimentary tickets to opening day or 6+ games for an exclusive batting practice experience!",
        hideOfferFromModal: true,
      },
      {
        id: 3,
        discount: 0,
        minSelectedAmount: 5,
        updatedAccountGroup: 0,
        customProgressBarMessage:
          "Congrats! You've unlocked 4 complimentary tickets to Opening Day! Add 5 more games to unlock an exclusive batting practice experience or checkout now.",
        customAditionalMessage:
          " to receive 4 complimentary Opening Day tickets,",
      },
      {
        id: 4,
        discount: 0,
        minSelectedAmount: 6,
        updatedAccountGroup: 0,
        customProgressBarMessage:
          "Add 4+ games to unlock an exclusive batting practice experience.",
        hideOfferFromModal: true,
      },
      {
        id: 5,
        discount: 0,
        minSelectedAmount: 7,
        updatedAccountGroup: 0,
        customProgressBarMessage:
          "Add 3+ games to unlock an exclusive batting practice experience.",
        hideOfferFromModal: true,
      },
      {
        id: 6,
        discount: 0,
        minSelectedAmount: 8,
        updatedAccountGroup: 0,
        customProgressBarMessage:
          "Add 2+ games to unlock an exclusive batting practice experience.",
        hideOfferFromModal: true,
      },
      {
        id: 7,
        discount: 0,
        minSelectedAmount: 9,
        updatedAccountGroup: 0,
        customProgressBarMessage:
          "Add 1 more game to unlock an exclusive batting practice experience.",
        hideOfferFromModal: true,
      },
      {
        id: 8,
        discount: 0,
        minSelectedAmount: 10,
        updatedAccountGroup: 0,
        customProgressBarMessage:
          "Congrats! You have unlocked the opportunity to take batting practice on the field! Add more games or checkout now.",
        customAditionalMessage:
          " for an exclusive batting practice experience, or checkout now?",
      },
    ],
  },

  // seat map configs
  seatMapConfigs: {
    mapId: "",
    venueId: "",
    isView3dButtonHidden: true,
    staticSeatMapImage: StaticSeatMap.src,
    isShowAvailableSeatsButtonHidden: true,
    customSeatMapStaticImageStyle: {
      width: "60%",
      display: "block",
      marginX: "auto",
    },
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 3,
    maxGameSelectionCount: 15,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: true,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: true,
    defaultSeatCount: 2,
    checkoutTimerEnabled: false,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: true,

  // urls
  accountManagerUrl:
    "https://mlb.tickets.com/?agency=POSM_MYTIXX&orgid=56874#/auth/login",

  // vendor
  platform: vendorUUIDs.tickesdotcom,

  // okta config
  oktaIssuer: "https://ids.mlb.com/oauth2/aus1m088yK07noBfh356",
  oktaClientId: "0oasmcdgh92Jom7TI357",

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    authBackgroundImage: "",
    checkoutStatusPageImage: "",
    findGamesBannerImage: FindGamesBanner.src,
    logoImage: LogoImage + "?width=128&height=66",
    logoNameImage: "",
    faviconImage: LogoImage,
    seatMap3dPreviewImage: "",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },
    bottomBar: {
      showGameSelectionProgress: true,
    },

    gameFiltersSettings: {
      hideSectionFilter: false,
      hideAccessibilityFilter: false,
    },

    shouldDisplayServiceFee: false,
    shouldAddServiceFeeWithPerkPrice: true,

    customAdaLabel: (
      <>
        To purchase accessible seating locations, please contact the Box Office
        at{" "}
        <Link
          href="tel:2078799500"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (207) 879-9500
        </Link>
        .
      </>
    ),
  },

  colors: {
    primary: "#e03a3e",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,

    purchaseSeats: {
      similarSeatModalBody: (seatTitle: string) => (
        <>
          We are going to apply the same or similar seats to all games in your
          pack. Seat locations will be visible on the checkout page.
        </>
      ),
      progressBarText:
        "Add 3 games to unlock your official League x Sea Dogs Collab scarf, 5 games to receive 4 complimentary tickets to Opening Day, or 10+ games for an exclusive batting practice experience.",
      progressBarAditionalText:
        " to unlock your official League x Sea Dogs Collab scarf!",
    },

    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Your Portland Sea Dogs tickets are confirmed! You will be notified when your tickets are available to be managed.",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your mlb.com account to continue with seamless access.",
  },
};

export default config;
